/**
 * CSV import for measured sample data (PRD §4.1).
 *
 * Treated as untrusted input like any other file (§6.5): size-limited, and
 * parsed without ever being evaluated. The parser is deliberately forgiving
 * about FORMAT — semicolon or comma or tab separators, headers or not, blank
 * lines, a UTF-8 BOM, European decimal commas — and deliberately strict about
 * CONTENT, reporting exactly which cells it could not read rather than quietly
 * dropping them. Silently discarding unparseable measurements would bias the
 * distribution toward whatever happened to be machine-readable.
 */

export const MAX_CSV_BYTES = 16 * 1024 * 1024;
export const MAX_SAMPLES = 1_000_000;

export class CsvError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CsvError';
  }
}

export interface CsvColumn {
  name: string;
  /** Index among the detected columns. */
  index: number;
  values: number[];
  /** Cells that were present but unreadable, as 1-based line numbers. */
  skipped: number[];
}

export interface CsvParseResult {
  columns: CsvColumn[];
  rowCount: number;
  hadHeader: boolean;
  delimiter: string;
  notes: string[];
}

function detectDelimiter(sample: string): string {
  const candidates = [',', ';', '\t', '|'];
  let best = ',';
  let bestScore = -1;

  for (const d of candidates) {
    // Score by consistency of field count across lines, not raw frequency: a
    // decimal comma inside numbers is frequent but inconsistent.
    const lines = sample.split(/\r?\n/).filter((l) => l.trim() !== '').slice(0, 20);
    if (lines.length === 0) continue;
    const counts = lines.map((l) => l.split(d).length);
    const first = counts[0];
    if (first < 2) continue;
    const consistent = counts.filter((c) => c === first).length / counts.length;
    const score = consistent * Math.min(first, 8);
    if (score > bestScore) {
      bestScore = score;
      best = d;
    }
  }
  return best;
}

/**
 * Parse a number, tolerating the formats measurement equipment actually emits.
 * Returns NaN when the cell holds no readable number.
 */
function parseCell(raw: string, allowDecimalComma: boolean): number {
  let s = raw.trim().replace(/^["']|["']$/g, '').trim();
  if (s === '') return NaN;

  // Thousands separators and a European decimal comma, but never both meanings
  // at once — that ambiguity is resolved once for the whole file by the caller.
  if (allowDecimalComma) s = s.replace(/\./g, '').replace(',', '.');
  else s = s.replace(/,/g, '');

  // Strip a trailing unit suffix, e.g. "10.2 mm" or "4k7".
  const m = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?/.exec(s);
  if (!m) return NaN;
  const value = Number(m[0]);
  return Number.isFinite(value) ? value : NaN;
}

export function parseCsv(text: string): CsvParseResult {
  if (text.length > MAX_CSV_BYTES) {
    throw new CsvError(
      `File is too large (${(text.length / 1e6).toFixed(1)} MB, limit ${MAX_CSV_BYTES / 1e6} MB)`,
    );
  }

  const clean = text.replace(/^﻿/, '');
  const lines = clean.split(/\r?\n/);
  const nonEmpty = lines.filter((l) => l.trim() !== '');
  if (nonEmpty.length === 0) throw new CsvError('File is empty');

  const delimiter = detectDelimiter(clean.slice(0, 8192));
  const notes: string[] = [];

  // Decide the decimal convention once for the whole file. A comma is the
  // decimal mark only if using it as a separator would produce ragged rows.
  const fieldCounts = nonEmpty.slice(0, 20).map((l) => l.split(delimiter).length);
  const ragged = new Set(fieldCounts).size > 1;
  const allowDecimalComma =
    delimiter !== ',' && /\d,\d/.test(clean.slice(0, 8192)) && !/\d\.\d/.test(clean.slice(0, 8192));
  if (allowDecimalComma) notes.push('Comma read as the decimal mark.');
  if (ragged) notes.push('Rows have differing field counts; short rows were padded.');

  // A header is present when the first row has no readable numbers but later
  // rows do.
  const firstFields = nonEmpty[0].split(delimiter);
  const firstNumeric = firstFields.filter((f) => !Number.isNaN(parseCell(f, allowDecimalComma)));
  const hadHeader = firstNumeric.length === 0 && nonEmpty.length > 1;

  const headerNames = hadHeader
    ? firstFields.map((f, i) => f.trim().replace(/^["']|["']$/g, '') || `Column ${i + 1}`)
    : firstFields.map((_, i) => `Column ${i + 1}`);

  const columns: CsvColumn[] = headerNames.map((name, index) => ({
    name,
    index,
    values: [],
    skipped: [],
  }));

  let rowCount = 0;
  const startLine = hadHeader ? 1 : 0;
  const dataLines = nonEmpty.slice(startLine);

  for (let r = 0; r < dataLines.length; r++) {
    const fields = dataLines[r].split(delimiter);
    rowCount++;
    for (let c = 0; c < columns.length; c++) {
      const raw = fields[c];
      if (raw === undefined) continue;
      const value = parseCell(raw, allowDecimalComma);
      if (Number.isNaN(value)) {
        if (raw.trim() !== '') columns[c].skipped.push(r + startLine + 1);
      } else {
        columns[c].values.push(value);
      }
    }
    if (rowCount > MAX_SAMPLES) {
      throw new CsvError(`Too many rows (limit ${MAX_SAMPLES.toLocaleString('en-US')})`);
    }
  }

  const usable = columns.filter((c) => c.values.length >= 2);
  if (usable.length === 0) {
    throw new CsvError(
      'No column contained at least two readable numbers. Check the delimiter and that the ' +
        'file holds measurements rather than formatted text.',
    );
  }

  for (const c of usable) {
    if (c.skipped.length > 0) {
      notes.push(
        `Column "${c.name}": ${c.skipped.length} cell${c.skipped.length === 1 ? '' : 's'} ` +
          `could not be read (line${c.skipped.length === 1 ? '' : 's'} ${c.skipped
            .slice(0, 5)
            .join(', ')}${c.skipped.length > 5 ? ', …' : ''}).`,
      );
    }
  }

  return { columns: usable, rowCount, hadHeader, delimiter, notes };
}

/** Descriptive statistics shown before committing an import. */
export function describeSamples(values: number[]): {
  n: number;
  mean: number;
  stdDev: number;
  min: number;
  max: number;
  median: number;
} {
  const n = values.length;
  const sorted = [...values].sort((a, b) => a - b);
  let sum = 0;
  for (const v of values) sum += v;
  const mean = sum / n;
  let ss = 0;
  for (const v of values) ss += (v - mean) ** 2;
  return {
    n,
    mean,
    stdDev: Math.sqrt(ss / Math.max(1, n - 1)),
    min: sorted[0],
    max: sorted[n - 1],
    median:
      n % 2 === 1 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2,
  };
}
